import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Globe,
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash2,
  Clock,
  ChevronDown,
  Loader2,
  FileCode,
  Palette,
  Code2,
  RefreshCw,
  ExternalLink,
  Home,
  X,
  Image,
  Copy,
  Braces,
  Monitor,
  Tablet,
  Smartphone,
  Layers,
  Layout,
  RotateCcw,
  RotateCw,
  Search,
  Edit2,
  Eye,
  Type,
  Check,
  Square,
} from 'lucide-react'
import { Button } from '../ui/button'
import LoadingSpinner from '../common/LoadingSpinner'
import { cn } from '../../utils/cn'
import { INDUSTRIES } from '../../utils/industrySchemas'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import { NSSelect } from '../shared/ns-kit'
import '../../styles/editor-palette.css'

// ─── Constants ────────────────────────────────────────────────────────────────

const DEVICE_SIZES = {
  desktop: { width: '100%',  label: 'Desktop', icon: Monitor },
  tablet:  { width: '768px', label: 'Tablet',  icon: Tablet },
  mobile:  { width: '390px', label: 'Mobile',  icon: Smartphone },
}

const CODE_TABS = [
  { id: 'html', label: 'HTML', language: 'html',       icon: FileCode },
  { id: 'css',  label: 'CSS',  language: 'css',        icon: Palette },
  { id: 'js',   label: 'JS',   language: 'javascript', icon: Code2 },
]

// Monaco theme matching the prototype's dark code surface (#0B0D1A)
const MONACO_DARK = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'comment', foreground: '5C6079', fontStyle: 'italic' },
    { token: 'tag', foreground: '5BC0EB' },
    { token: 'attribute.name', foreground: 'FFB454' },
    { token: 'attribute.value', foreground: '2BD9A6' },
    { token: 'string', foreground: '2BD9A6' },
    { token: 'keyword', foreground: 'FF8A65' },
    { token: 'number', foreground: 'A78BFA' },
    { token: 'delimiter', foreground: '7B8198' },
  ],
  colors: {
    'editor.background': '#0B0D1A',
    'editor.foreground': '#D7DAEC',
    'editorLineNumber.foreground': '#3A3F5A',
    'editorLineNumber.activeForeground': '#7B8198',
    'editor.selectionBackground': '#7C5CFF44',
    'editor.lineHighlightBackground': '#FFFFFF08',
    'editorCursor.foreground': '#7C5CFF',
    'editorGutter.background': '#0B0D1A',
  },
}

const LAYER_ICON = { section: Square, div: Square, header: Layout, footer: Layout, nav: Layout, h1: Type, h2: Type, h3: Type, h4: Type, p: Type, span: Type, a: Type, button: Square, img: Image, ul: Layers, ol: Layers, li: Square, form: Square }

// ─── Inspector sub-components (match prototype) ─────────────────────────────────

function InspectorSection({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  )
}

function PropRow({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr)', gap: 10, alignItems: 'center' }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}

function SmallInput({ label, value, suffix, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)' }}>
      {label && <span style={{ fontSize: 10.5, color: 'var(--text-faint)', width: 12 }}>{label}</span>}
      <input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none', padding: 0 }}
      />
      {suffix && <span style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>{suffix}</span>}
    </div>
  )
}

function ColorPick({ value, onChange }) {
  const isTransparent = !value || value === 'transparent'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)' }}>
      <label style={{ width: 18, height: 18, borderRadius: 5, border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0, background: isTransparent ? 'linear-gradient(45deg,#999 25%,transparent 25%,transparent 75%,#999 75%) 0 0 / 8px 8px, white' : value }}>
        <input type="color" value={isTransparent ? '#000000' : value} onChange={(e) => onChange?.(e.target.value)} style={{ opacity: 0, width: 0, height: 0 }} />
      </label>
      <input value={value || ''} onChange={(e) => onChange?.(e.target.value)} style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 11.5, outline: 'none' }} />
    </div>
  )
}

// Flatten a parsed layer tree into rows with depth
function flattenLayers(nodes, depth = 0, out = []) {
  nodes.forEach((n) => {
    out.push({ ...n, depth })
    if (n.children?.length) flattenLayers(n.children, depth + 1, out)
  })
  return out
}

// Walk a body node and assign sequential data-wn-id to every element (skipping
// script/style), recursing to depth 4. Used identically by the preview build and
// the WYSIWYG source write-back so the IDs always line up.
function assignWnIds(root) {
  let counter = 0
  const walk = (node, depth) => {
    Array.from(node.children).forEach((c) => {
      const tag = c.tagName.toLowerCase()
      if (tag === 'script' || tag === 'style') return
      c.setAttribute('data-wn-id', String(++counter))
      if (depth < 4) walk(c, depth + 1)
    })
  }
  walk(root, 0)
}

// Was the raw HTML a full document (<html>…) or a fragment of body markup?
function isFullDocument(html) {
  return /<html[\s>]/i.test(html || '')
}

const escHtml = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Preview-only WYSIWYG: substitute scalar {{token}} placeholders with their
// values so the editor shows the same content the live site renders (see the
// backend's buildTemplateContext). Values are HTML-escaped — like Mustache's
// {{ }} — and only double-stache, non-section tags are touched, so element
// structure is preserved and the inspector's data-wn-id mapping (which walks
// the raw, unrendered HTML on write-back) stays in sync. Section tags
// ({{#x}}/{{/x}}), triple-stache and unescaped {{&x}} are deliberately left
// alone (they'd change structure / need array data). Unset tokens are kept
// visible so the user can still see which variables remain to fill.
function renderPreviewVars(html, ctx) {
  if (!html || !ctx) return html
  return html.replace(/(?<!\{)\{\{\s*([\w.]+)\s*\}\}(?!\})/g, (match, key) => {
    const v = ctx[key]
    return (v === undefined || v === null || v === '') ? match : escHtml(v)
  })
}

// ─── Site picker helpers (Visual Editor "select a site" screen) ─────────────────
const PICK_GRADIENTS = [
  ['#7C5CFF', '#5B7CFF'], ['#A78BFA', '#7C5CFF'], ['#EC4899', '#A78BFA'], ['#FF8A65', '#F76C5E'],
  ['#F76C5E', '#FFB454'], ['#2BD9A6', '#06B6D4'], ['#06B6D4', '#5B7CFF'], ['#FFB454', '#FF8A65'],
  ['#A78BFA', '#EC4899'], ['#5BC0EB', '#5B7CFF'], ['#2BD9A6', '#FFB454'], ['#7C5CFF', '#06B6D4'],
]
function pickGradient(s) {
  const key = String(s.id || s.name || '')
  const hash = key.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return PICK_GRADIENTS[hash % PICK_GRADIENTS.length]
}
const PICK_STATUS = {
  published: { tone: 'success', label: 'Published', dot: true },
  draft:     { tone: '',        label: 'Draft' },
  archived:  { tone: 'info',    label: 'Archived' },
}
function relativeTime(ts) {
  if (!ts) return '—'
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d === 1) return 'yesterday'
  if (d < 7) return `${d}d ago`
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Brand sub-components (match prototype) ─────────────────────────────────────

function BrandRow({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, alignItems: 'center' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}

function AssetSlot({ value, small, onReplace, onClear }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 9 }}>
      <div style={{ width: small ? 28 : 40, height: small ? 28 : 40, borderRadius: 6, flexShrink: 0, background: value ? 'var(--bg-2)' : 'var(--surface-2)', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {value
          ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <Image size={small ? 13 : 16} style={{ color: 'var(--text-faint)' }} />}
      </div>
      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value ? value.split('/').pop() : 'none'}
      </span>
      <button className="btn btn-sm" onClick={onReplace}>Replace</button>
      {value && <button className="site-action" onClick={onClear} title="Remove"><X size={13} /></button>}
    </div>
  )
}

function SwatchRow({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {options.map((c) => (
        <button
          key={c}
          title={c}
          onClick={() => onChange(c)}
          style={{
            width: 28, height: 28, borderRadius: 7, background: c,
            border: c.toUpperCase() === (value || '').toUpperCase() ? '2px solid var(--text)' : '2px solid var(--border)',
            boxShadow: c.toUpperCase() === (value || '').toUpperCase() ? '0 0 0 2px var(--bg-1) inset' : 'none',
            cursor: 'pointer',
          }}
        />
      ))}
      <input className="input mono" value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12, marginLeft: 6, textTransform: 'uppercase' }} />
    </div>
  )
}

// ─── NewEditor ────────────────────────────────────────────────────────────────

const NewEditor = () => {
  const navigate      = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient   = useQueryClient()
  const { theme }     = useTheme()
  const { user }      = useAuth()

  // Client editing is the same editor, locked down: no code tabs, content-only
  // inspector, and client-scoped navigation paths.
  const isClient    = user?.role === 'client'
  const editorPath  = isClient ? '/client/editor' : '/admin/editor'
  const sitesPath   = isClient ? '/client/sites' : '/admin/sites'

  const siteId   = searchParams.get('site')
  const clientId = searchParams.get('client')

  // ── Editor state ──────────────────────────────────────────────────────────
  const [editorMode,      setEditorMode]      = useState('visual')
  const [activeCodeTab,   setActiveCodeTab]   = useState('html')
  const [device,          setDevice]          = useState('desktop')
  const [hasUnsaved,      setHasUnsaved]      = useState(false)
  const [isPublishing,    setIsPublishing]    = useState(false)
  const [previewKey,      setPreviewKey]      = useState(0)
  const [publicUrl,       setPublicUrl]       = useState(null)
  const [lastSaved,       setLastSaved]       = useState(null)

  // ── Sidebar state ─────────────────────────────────────────────────────────
  const [sidebarTab,      setSidebarTab]      = useState('Pages')

  // ── Pages state ───────────────────────────────────────────────────────────
  const [pages,           setPages]           = useState([])
  const [activePage,      setActivePage]      = useState(null)
  const [showAddPage,     setShowAddPage]     = useState(false)
  const [newPageName,     setNewPageName]     = useState('')
  const [newPageSlug,     setNewPageSlug]     = useState('')
  const [deletingPageId,  setDeletingPageId]  = useState(null)

  // ── Panels state (for code-editor sub-panels) ─────────────────────────────
  const [showBrandPalette, setShowBrandPalette] = useState(false)
  const [showVarsPanel,    setShowVarsPanel]    = useState(false)
  const [varsTab,          setVarsTab]          = useState(null)
  const [showMediaPicker,  setShowMediaPicker]  = useState(false)

  // ── Site search state (no-site screen) ───────────────────────────────────
  const [searchQuery,    setSearchQuery]    = useState('')
  const [filteredSites,  setFilteredSites]  = useState([])

  // ── Code state ────────────────────────────────────────────────────────────
  const [code, setCode] = useState({ html: '', css: '', js: '' })
  const codeRef         = useRef({ html: '', css: '', js: '' })
  const iframeRef       = useRef(null)
  const editorRefs      = useRef({})

  // ── Breadcrumb page picker ─────────────────────────────────────────────────
  const [pageMenuOpen, setPageMenuOpen] = useState(false)
  const pageMenuRef = useRef(null)

  // ── Visual editing state (click-select + live tweak + WYSIWYG write-back) ──
  const [selectedEl, setSelectedEl] = useState(null) // { id, tag, text, hasText, styles:{} }
  // The iframe shows `rendered.doc`, keyed on `rendered.ver`. Each adoption bumps
  // ver, which REMOUNTS the iframe — the only reliable way to force a reload
  // (changing srcDoc on an existing iframe doesn't always reload). It tracks
  // previewDoc except during active inspector edits (live via postMessage).
  const [rendered, setRendered] = useState({ doc: '', ver: 0 })
  const previewDocRef = useRef('')
  const editsRef       = useRef({})   // { [wnId]: { text?, styles?: {prop:value} } }
  const commitTimerRef = useRef(null)
  const commitRef      = useRef(null) // always points at latest commitSourceEdits

  // ── Undo / redo history (snapshots of {html,css,js}) ──────────────────────
  const histRef      = useRef({ stack: [], ptr: -1 })
  const histTimerRef = useRef(null)
  const restoringRef = useRef(false)
  const [histVer, setHistVer] = useState(0) // bump to re-render undo/redo enabled state

  const [showPreview, setShowPreview] = useState(false) // full-screen preview overlay

  // ── Brand & variables (persisted in site.meta_data) ───────────────────────
  const [brand, setBrand] = useState({
    siteName: '', tagline: '', logo: '', favicon: '',
    primary: '#7C5CFF', accent: '#5B7CFF', background: '#FFFFFF',
    headingFont: 'Geist', bodyFont: 'Inter', scale: 'Modern',
  })
  const [varValues, setVarValues] = useState({})   // { token: value }
  const [editingVar, setEditingVar] = useState(null) // key currently being edited
  const [editingVarValue, setEditingVarValue] = useState('')
  const [assetTarget, setAssetTarget] = useState(null) // 'logo' | 'favicon' when picking brand asset

  // post a message into the preview iframe
  const postToIframe = useCallback((msg) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
  }, [])

  // ── Monaco theme ──────────────────────────────────────────────────────────
  const isDark = theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  const monacoTheme = isDark ? 'wn-dark' : 'light'
  const handleEditorBeforeMount = useCallback((monaco) => {
    try { monaco.editor.defineTheme('wn-dark', MONACO_DARK) } catch { /* already defined */ }
  }, [])

  // ── Sync editorMode → activeCodeTab ──────────────────────────────────────
  useEffect(() => {
    if (['html', 'css', 'js'].includes(editorMode)) {
      setActiveCodeTab(editorMode)
    }
  }, [editorMode])

  // ── Site search query (only when no ?site=) ───────────────────────────────
  const { data: allSites = [] } = useQuery(
    'all-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { enabled: !siteId }
  )

  useEffect(() => {
    if (!siteId && allSites.length > 0 && filteredSites.length === 0 && searchQuery === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    }
  }, [allSites, siteId])

  const handleSearchChange = (e) => {
    const q = e.target.value
    setSearchQuery(q)
    if (q.trim() === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    } else {
      setFilteredSites(
        allSites.filter(s =>
          s.name.toLowerCase().includes(q.toLowerCase()) ||
          (s.company_name || '').toLowerCase().includes(q.toLowerCase())
        )
      )
    }
  }

  const handleSiteSelect = (s) => {
    navigate(isClient ? `${editorPath}?site=${s.id}` : `${editorPath}?site=${s.id}&client=${s.client_id}`)
  }

  // ── Reset on site change ─────────────────────────────────────────────────
  useEffect(() => {
    setActivePage(null)
    setPages([])
    setCode({ html: '', css: '', js: '' })
    codeRef.current = { html: '', css: '', js: '' }
    setHasUnsaved(false)
    setPublicUrl(null)
    setLastSaved(null)
  }, [siteId])

  // ── Load site ────────────────────────────────────────────────────────────
  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  // ── Load client media (clientId from URL, falling back to the loaded site) ─
  const effectiveClientId = clientId || site?.client_id || null
  const { data: mediaItems = [] } = useQuery(
    ['media', effectiveClientId],
    () => apiService.get('/media', { params: { clientId: effectiveClientId } }).then(r => r.data.media || []),
    { enabled: !!effectiveClientId }
  )

  // ── Load pages ────────────────────────────────────────────────────────────
  const { data: pagesData, isLoading: pagesLoading, refetch: refetchPages } = useQuery(
    ['pages', siteId],
    () => apiService.get(`/sites/${siteId}/pages`).then(r => r.data.pages || []),
    { enabled: !!siteId }
  )

  useEffect(() => {
    if (!pagesData || pagesData.length === 0) return
    setPages(pagesData)
    // Only auto-select a home page on first load (don't clobber an active page).
    if (activePage) return
    const home = pagesData.find(p => p.is_home) || pagesData[0]
    setActivePage(home)
    setCode(prev => ({ ...prev, html: home.html_content || '' }))
    codeRef.current = { ...codeRef.current, html: home.html_content || '' }
    editsRef.current = {}
    setPreviewKey(k => k + 1)
  }, [pagesData, activePage])

  // Load site-level CSS/JS + brand + variables from meta_data
  useEffect(() => {
    if (!site) return
    const siteCss = site.draft_css_content || site.css_content || ''
    const siteJs  = site.draft_js_content  || site.js_content  || ''
    setCode(prev => ({ ...prev, css: siteCss, js: siteJs }))
    codeRef.current = { ...codeRef.current, css: siteCss, js: siteJs }
    // CSS/JS arrive on their own effect — resync the (decoupled) preview iframe
    // so it doesn't render HTML without styles on first open.
    setPreviewKey(k => k + 1)

    let meta = {}
    try { meta = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : (site.meta_data || {}) } catch { meta = {} }
    const b = meta.brand || {}
    const palette = Array.isArray(site.brand_colors) ? site.brand_colors : []
    setBrand({
      siteName: b.siteName ?? site.name ?? '',
      tagline: b.tagline ?? '',
      logo: b.logo ?? meta.logo ?? '',
      favicon: b.favicon ?? site.favicon ?? '',
      primary: b.primary ?? palette[0] ?? '#7C5CFF',
      accent: b.accent ?? palette[1] ?? '#5B7CFF',
      background: b.background ?? '#FFFFFF',
      headingFont: b.headingFont ?? 'Geist',
      bodyFont: b.bodyFont ?? 'Inter',
      scale: b.scale ?? 'Modern',
    })
    setVarValues(meta.variables || {})
  }, [site?.id])

  // Seed the undo/redo baseline from the loaded data once page + site are ready
  // (reads html/css/js directly so it's complete regardless of effect order).
  useEffect(() => {
    if (!activePage || !site) return
    const snap = {
      html: activePage.html_content || '',
      css: site.draft_css_content || site.css_content || '',
      js: site.draft_js_content || site.js_content || '',
    }
    histRef.current = { stack: [snap], ptr: 0 }
    setHistVer(v => v + 1)
  }, [activePage?.id, site?.id])

  const loadPage = (page) => {
    editsRef.current = {}
    setActivePage(page)
    setCode(prev => ({ ...prev, html: page.html_content || '' }))
    codeRef.current = { ...codeRef.current, html: page.html_content || '' }
    setHasUnsaved(false)
    setSelectedEl(null)
    setPreviewKey(k => k + 1)
    // history baseline is reseeded by the dedicated [activePage,site] effect
  }

  // ── Save mutations ────────────────────────────────────────────────────────
  const savePageMutation = useMutation(
    ({ pageId, payload }) => apiService.put(`/sites/${siteId}/pages/${pageId}`, payload),
    {
      onSuccess: () => {
        toast.success('Saved')
        setHasUnsaved(false)
        setLastSaved(new Date())
        refetchPages()
      },
      onError: () => toast.error('Failed to save page'),
    }
  )

  const saveSiteMutation = useMutation(
    (payload) => apiService.put(`/sites/${siteId}`, payload),
    {
      onError: () => toast.error('Failed to save global CSS/JS'),
    }
  )

  // ── Save brand / variables into site.meta_data ────────────────────────────
  const saveMetaMutation = useMutation(
    (patch) => {
      let meta = {}
      try { meta = typeof site?.meta_data === 'string' ? JSON.parse(site.meta_data) : (site?.meta_data || {}) } catch { meta = {} }
      const merged = { ...meta, ...patch }
      return apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify(merged) })
    },
    {
      onSuccess: () => { toast.success('Saved'); queryClient.invalidateQueries(['site', siteId]) },
      onError: () => toast.error('Failed to save'),
    }
  )

  const saveBrand = useCallback(() => saveMetaMutation.mutate({ brand }), [brand, saveMetaMutation])
  const saveVariables = useCallback((next) => saveMetaMutation.mutate({ variables: next ?? varValues }), [varValues, saveMetaMutation])

  // ── Add page mutation ─────────────────────────────────────────────────────
  const addPageMutation = useMutation(
    (payload) => apiService.post(`/sites/${siteId}/pages`, payload),
    {
      onSuccess: (res) => {
        toast.success(`Page "${res.data.page.name}" created`)
        setShowAddPage(false)
        setNewPageName('')
        setNewPageSlug('')
        refetchPages().then(({ data }) => {
          const created = data?.pages?.find(p => p.id === res.data.page.id)
          if (created) loadPage(created)
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to create page')
      },
    }
  )

  // ── Delete page mutation ──────────────────────────────────────────────────
  const deletePageMutation = useMutation(
    (pageId) => apiService.delete(`/sites/${siteId}/pages/${pageId}`),
    {
      onSuccess: (_, deletedId) => {
        toast.success('Page deleted')
        setDeletingPageId(null)
        refetchPages().then(({ data }) => {
          const remaining = data?.pages || []
          if (activePage?.id === deletedId && remaining.length > 0) {
            loadPage(remaining.find(p => p.is_home) || remaining[0])
          }
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete page')
        setDeletingPageId(null)
      },
    }
  )

  // ── Publish mutation ──────────────────────────────────────────────────────
  const publishMutation = useMutation(
    () => apiService.post(`/sites/${siteId}/publish`),
    {
      onSuccess: (res) => {
        toast.success('Site published successfully!')
        setIsPublishing(false)
        if (res.data?.public_url) setPublicUrl(res.data.public_url)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => {
        toast.error('Failed to publish site')
        setIsPublishing(false)
      },
    }
  )

  // ── Handlers ──────────────────────────────────────────────────────────────
  // Debounced checkpoint — pushes the current code snapshot onto the undo stack
  const checkpoint = useCallback(() => {
    if (restoringRef.current) return
    if (histTimerRef.current) clearTimeout(histTimerRef.current)
    histTimerRef.current = setTimeout(() => {
      const snap = { ...codeRef.current }
      const h = histRef.current
      const cur = h.stack[h.ptr]
      if (cur && cur.html === snap.html && cur.css === snap.css && cur.js === snap.js) return
      // truncate any redo branch, push new snapshot
      h.stack = h.stack.slice(0, h.ptr + 1)
      h.stack.push(snap)
      if (h.stack.length > 60) h.stack.shift()
      h.ptr = h.stack.length - 1
      setHistVer(v => v + 1)
    }, 450)
  }, [])

  const restoreSnapshot = useCallback((snap) => {
    // Cancel any pending commit/checkpoint so they don't fire against the restored state
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null }
    if (histTimerRef.current) { clearTimeout(histTimerRef.current); histTimerRef.current = null }
    restoringRef.current = true
    editsRef.current = {}
    codeRef.current = { ...codeRef.current, ...snap }
    setCode(prev => ({ ...prev, ...snap }))
    setHasUnsaved(true)
    setSelectedEl(null)
    setPreviewKey(k => k + 1)            // force iframe remount with restored doc
    // keep suppressing checkpoints until past the debounce window
    setTimeout(() => { restoringRef.current = false }, 500)
  }, [])

  const undo = useCallback(() => {
    const h = histRef.current
    if (h.ptr <= 0) return
    h.ptr -= 1
    restoreSnapshot(h.stack[h.ptr])
    setHistVer(v => v + 1)
  }, [restoreSnapshot])

  const redo = useCallback(() => {
    const h = histRef.current
    if (h.ptr >= h.stack.length - 1) return
    h.ptr += 1
    restoreSnapshot(h.stack[h.ptr])
    setHistVer(v => v + 1)
  }, [restoreSnapshot])

  const canUndo = histRef.current.ptr > 0
  const canRedo = histRef.current.ptr < histRef.current.stack.length - 1

  const handleCodeChange = useCallback((value, lang) => {
    codeRef.current = { ...codeRef.current, [lang]: value || '' }
    setCode(prev => ({ ...prev, [lang]: value || '' }))
    setHasUnsaved(true)
    checkpoint()
  }, [checkpoint])

  const refreshPreview = useCallback(() => setPreviewKey(k => k + 1), [])

  const handleSave = useCallback(() => {
    // Flush any pending visual edits into the HTML source first
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null }
    commitRef.current?.()
    saveSiteMutation.mutate({
      css_content: codeRef.current.css,
      js_content:  codeRef.current.js,
    })
    if (activePage) {
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
    }
  }, [activePage])

  const handlePageSwitch = useCallback((page) => {
    if (page.id === activePage?.id) return
    // Flush pending visual edits into source before reading codeRef
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null }
    commitRef.current?.()
    if ((hasUnsaved || Object.keys(editsRef.current).length) && activePage) {
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
      saveSiteMutation.mutate({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      })
    }
    loadPage(page)
  }, [activePage, hasUnsaved])

  // Close the breadcrumb page picker on outside-click / Escape
  useEffect(() => {
    if (!pageMenuOpen) return
    const onDown = (e) => { if (pageMenuRef.current && !pageMenuRef.current.contains(e.target)) setPageMenuOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setPageMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [pageMenuOpen])

  const handlePublish = useCallback(async () => {
    setIsPublishing(true)
    const savePromises = [
      saveSiteMutation.mutateAsync({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      }),
    ]
    if (activePage) {
      savePromises.push(savePageMutation.mutateAsync({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      }))
    }
    await Promise.all(savePromises).catch(() => {})
    publishMutation.mutate()
  }, [hasUnsaved, activePage])

  const handleAddPage = () => {
    if (!newPageName.trim()) return
    const slug = newPageSlug.trim() || `/${newPageName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`
    addPageMutation.mutate({ name: newPageName.trim(), slug })
  }

  const handlePageNameChange = (e) => {
    const name = e.target.value
    setNewPageName(name)
    setNewPageSlug(`/${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`)
  }

  // ── Insert helpers ────────────────────────────────────────────────────────
  const insertTextAtCursor = useCallback((text) => {
    const editor = editorRefs.current[activeCodeTab]
    if (editor) {
      const selection = editor.getSelection()
      editor.executeEdits('insert-var', [{ range: selection, text, forceMoveMarkers: true }])
      editor.focus()
    } else {
      navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    }
  }, [activeCodeTab])

  const insertMediaAtCursor = useCallback((url) => {
    const editor = editorRefs.current[activeCodeTab]
    if (!editor) {
      navigator.clipboard.writeText(url)
      toast.success('URL copied to clipboard')
      setShowMediaPicker(false)
      return
    }
    const selection = editor.getSelection()
    const text = activeCodeTab === 'html'
      ? `<img src="${url}" alt="" />`
      : activeCodeTab === 'css'
      ? `url('${url}')`
      : `"${url}"`
    editor.executeEdits('media-picker', [{ range: selection, text, forceMoveMarkers: true }])
    editor.focus()
    setShowMediaPicker(false)
  }, [activeCodeTab])

  // ── PostMessage: iframe link interception + element selection ─────────────
  useEffect(() => {
    const handler = (e) => {
      const d = e.data
      if (!d || !d.type) return
      if (d.type === 'navigate') {
        const slug = d.slug
        const target = pages.find(p => p.slug === slug || p.slug === `/${slug.replace(/^\//, '')}`)
        if (target) handlePageSwitch(target)
        else toast(`Page "${slug}" not found in this site`, { icon: '🔗' })
      } else if (d.type === 'wn-select') {
        setSelectedEl(d.payload)
      } else if (d.type === 'wn-deselect') {
        setSelectedEl(null)
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [pages, handlePageSwitch])

  // Commit accumulated visual edits back into the page HTML source (WYSIWYG).
  // Parses codeRef.html, re-assigns the same wn-ids, applies recorded text/style
  // edits as inline styles, serialises (stripping wn-ids), and updates code state.
  const commitSourceEdits = useCallback(() => {
    const edits = editsRef.current
    if (!edits || Object.keys(edits).length === 0) return
    const rawHtml = codeRef.current.html || ''
    const parsed = new DOMParser().parseFromString(rawHtml, 'text/html')
    assignWnIds(parsed.body)
    Object.entries(edits).forEach(([wnId, change]) => {
      const node = parsed.querySelector(`[data-wn-id="${wnId}"]`)
      if (!node) return
      if (change.styles) {
        Object.entries(change.styles).forEach(([prop, value]) => { node.style[prop] = value })
      }
      // Only write text back for leaf text elements (no child elements) to avoid
      // wiping nested markup.
      if (typeof change.text === 'string' && node.children.length === 0) {
        node.textContent = change.text
      }
    })
    parsed.querySelectorAll('[data-wn-id]').forEach(n => n.removeAttribute('data-wn-id'))
    const out = isFullDocument(rawHtml)
      ? '<!DOCTYPE html>' + parsed.documentElement.outerHTML
      : parsed.body.innerHTML
    editsRef.current = {}
    codeRef.current = { ...codeRef.current, html: out }
    setCode(prev => ({ ...prev, html: out }))
    setHasUnsaved(true)
    checkpoint()
  }, [checkpoint])
  commitRef.current = commitSourceEdits

  const scheduleCommit = useCallback(() => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current)
    commitTimerRef.current = setTimeout(() => commitSourceEdits(), 350)
  }, [commitSourceEdits])

  const recordEdit = useCallback((id, patch) => {
    const cur = editsRef.current[id] || {}
    editsRef.current[id] = {
      ...cur,
      ...(patch.text !== undefined ? { text: patch.text } : {}),
      ...(patch.styles ? { styles: { ...(cur.styles || {}), ...patch.styles } } : {}),
    }
    scheduleCommit()
  }, [scheduleCommit])

  // Apply a live style change to the selected element (instant via postMessage),
  // record it for the debounced source write-back.
  const applyStyle = useCallback((prop, value) => {
    if (!selectedEl) return
    postToIframe({ type: 'wn-set-style', id: selectedEl.id, prop, value })
    setSelectedEl(prev => prev ? { ...prev, styles: { ...prev.styles, [prop]: value } } : prev)
    recordEdit(selectedEl.id, { styles: { [prop]: value } })
  }, [selectedEl, postToIframe, recordEdit])

  const applyText = useCallback((text) => {
    if (!selectedEl) return
    postToIframe({ type: 'wn-set-text', id: selectedEl.id, text })
    setSelectedEl(prev => prev ? { ...prev, text } : prev)
    recordEdit(selectedEl.id, { text })
  }, [selectedEl, postToIframe, recordEdit])

  // Update a "box" property (padding/margin) — sets the real CSS props in the
  // iframe, tracks the rounded value in the inspector, records for write-back.
  const applyBox = useCallback((trackKey, cssProps, raw) => {
    if (!selectedEl) return
    const px = `${parseInt(raw) || 0}px`
    const styles = {}
    cssProps.forEach(p => { postToIframe({ type: 'wn-set-style', id: selectedEl.id, prop: p, value: px }); styles[p] = px })
    setSelectedEl(prev => prev ? { ...prev, styles: { ...prev.styles, [trackKey]: parseInt(raw) || 0 } } : prev)
    recordEdit(selectedEl.id, { styles })
  }, [selectedEl, postToIframe, recordEdit])

  // ── Keyboard shortcuts: Cmd/Ctrl+S, and Cmd/Ctrl+Z/⇧Z in visual mode ──────
  useEffect(() => {
    const onKeyDown = (e) => {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key === 's') {
        e.preventDefault()
        handleSave()
      } else if (mod && e.key.toLowerCase() === 'z' && editorMode === 'visual') {
        // In code modes Monaco handles its own undo; only intercept in visual.
        e.preventDefault()
        if (e.shiftKey) redo(); else undo()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSave, editorMode, undo, redo])

  // ── Build preview document + layer tree ───────────────────────────────────
  // Parses the page HTML, assigns data-wn-id to every element (used to map
  // iframe selections ↔ the Layers tree), and injects the interactive agent.
  // Flatten saved meta + live brand/variables exactly like the backend's
  // buildTemplateContext, so the preview fills {{tokens}} the same way the
  // live site does. Live editor state (brand, varValues) wins over saved meta.
  const previewContext = useMemo(() => {
    let savedMeta = {}
    try { savedMeta = typeof site?.meta_data === 'string' ? JSON.parse(site.meta_data) : (site?.meta_data || {}) } catch { savedMeta = {} }
    const { stripeSecretKey, accessPassword, customCode, variables, brand: savedBrand, ...rest } = savedMeta
    return { ...rest, ...(savedBrand || {}), ...(variables || {}), ...brand, ...varValues, site_id: site?.id }
  }, [site, brand, varValues])

  const { previewDoc, layerTree } = useMemo(() => {
    const rawHtml = renderPreviewVars(
      code.html || '<!DOCTYPE html><html><head></head><body></body></html>',
      previewContext
    )
    const parser = new DOMParser()
    const parsed = parser.parseFromString(rawHtml, 'text/html')

    // Strip relative stylesheet links (CSS is inlined; relative paths 404 in srcdoc)
    parsed.querySelectorAll('link[rel="stylesheet"]').forEach((lnk) => {
      const href = lnk.getAttribute('href') || ''
      if (!href.includes('://')) lnk.remove()
    })

    // Assign data-wn-id (shared walk → IDs match the write-back), then read them
    // back to build the layer tree.
    assignWnIds(parsed.body)
    const buildTree = (node) => {
      const items = []
      Array.from(node.children).forEach((child) => {
        const id = child.getAttribute('data-wn-id')
        if (!id) return
        const textSnippet = (child.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 28)
        items.push({
          id, tag: child.tagName.toLowerCase(),
          label: child.id ? `#${child.id}` : (child.getAttribute('class')?.split(' ')[0] ? `.${child.getAttribute('class').split(' ')[0]}` : child.tagName.toLowerCase()),
          text: textSnippet,
          children: buildTree(child),
        })
      })
      return items
    }
    const layers = buildTree(parsed.body)

    // Inline CSS + agent CSS into <head>
    const headExtras =
      `<meta http-equiv="Content-Security-Policy" content="default-src * data: blob: 'unsafe-inline' 'unsafe-eval';">` +
      `<style>html,body{overscroll-behavior:none;}` +
      `[data-wn-hover]{outline:1.5px dashed rgba(124,92,255,.5)!important;outline-offset:-1px;cursor:default!important;}` +
      `[data-wn-sel]{outline:2px solid #7C5CFF!important;outline-offset:-1px;}` +
      `${code.css}</style>`
    parsed.head.insertAdjacentHTML('beforeend', headExtras)

    // Interactive agent: hover outline, click-to-select, live style/text updates, link nav
    const agent = `(function(){
      var selId=null;
      function byId(id){return document.querySelector('[data-wn-id="'+id+'"]');}
      function hex(c){var m=(c||'').match(/\\d+/g);if(!m)return c;if(m.length>=4&&m[3]==='0')return 'transparent';return '#'+m.slice(0,3).map(function(x){return ('0'+parseInt(x).toString(16)).slice(-2);}).join('');}
      document.addEventListener('mouseover',function(e){
        var t=e.target.closest('[data-wn-id]');
        document.querySelectorAll('[data-wn-hover]').forEach(function(n){n.removeAttribute('data-wn-hover');});
        if(t&&t.getAttribute('data-wn-id')!==selId)t.setAttribute('data-wn-hover','');
      },true);
      document.addEventListener('click',function(e){
        var a=e.target.closest('a[href]');
        if(a){var href=a.getAttribute('href')||'';
          if(href.startsWith('mailto:')||href.startsWith('tel:')){return;}
          if(href.startsWith('#')){e.preventDefault();e.stopPropagation();var tg=document.querySelector(href);if(tg){window.scrollTo({top:tg.getBoundingClientRect().top+window.pageYOffset,behavior:'smooth'});}return;}
          if(!href.startsWith('http')&&!href.startsWith('//')){e.preventDefault();e.stopPropagation();parent.postMessage({type:'navigate',slug:href},'*');return;}
        }
        var t=e.target.closest('[data-wn-id]');
        if(!t)return;
        e.preventDefault();e.stopPropagation();
        select(t);
      },true);
      function select(t){
        if(selId){var p=byId(selId);if(p)p.removeAttribute('data-wn-sel');}
        selId=t.getAttribute('data-wn-id');
        t.setAttribute('data-wn-sel','');
        t.removeAttribute('data-wn-hover');
        var cs=getComputedStyle(t);
        var hasText=Array.prototype.some.call(t.childNodes,function(n){return n.nodeType===3&&n.textContent.trim();});
        parent.postMessage({type:'wn-select',payload:{
          id:selId,tag:t.tagName.toLowerCase(),
          text:hasText?t.textContent.trim():'',hasText:hasText,
          styles:{
            color:hex(cs.color),background:hex(cs.backgroundColor),
            fontSize:Math.round(parseFloat(cs.fontSize))||16,
            fontWeight:cs.fontWeight,
            fontFamily:(cs.fontFamily||'').split(',')[0].replace(/['"]/g,'').trim(),
            paddingX:Math.round(parseFloat(cs.paddingLeft))||0,
            paddingY:Math.round(parseFloat(cs.paddingTop))||0,
            marginTop:Math.round(parseFloat(cs.marginTop))||0,
            marginBottom:Math.round(parseFloat(cs.marginBottom))||0,
            radius:Math.round(parseFloat(cs.borderTopLeftRadius))||0
          }
        }},'*');
      }
      window.addEventListener('message',function(e){
        var d=e.data;if(!d||!d.type)return;
        if(d.type==='wn-set-style'){var n=byId(d.id);if(n)n.style[d.prop]=d.value;}
        else if(d.type==='wn-set-text'){var n=byId(d.id);if(n)n.textContent=d.text;}
        else if(d.type==='wn-select-id'){var n=byId(d.id);if(n){select(n);n.scrollIntoView({behavior:'smooth',block:'center'});}}
        else if(d.type==='wn-deselect'){if(selId){var p=byId(selId);if(p)p.removeAttribute('data-wn-sel');selId=null;}}
      });
    })();`
    const scriptTag = `<script>${agent}\ntry{${code.js || ''}}catch(e){console.error(e);}<\/script>`
    parsed.body.insertAdjacentHTML('beforeend', scriptTag)

    return { previewDoc: '<!DOCTYPE html>' + parsed.documentElement.outerHTML, layerTree: layers }
  }, [code.html, code.css, code.js, previewContext])

  // The iframe shows `rendered.doc`, keyed on `rendered.ver` (remount = reload).
  // We only adopt the latest previewDoc on INTENTIONAL events (load, refresh,
  // page switch, undo/redo, entering visual) — never on live inspector edits,
  // which apply instantly via postMessage with no reload/flicker. previewDocRef
  // always holds the latest doc, so the next adopt reflects committed edits.
  previewDocRef.current = previewDoc
  const adoptPreview = useCallback(() => {
    setRendered(r => ({ doc: previewDocRef.current, ver: r.ver + 1 }))
  }, [])

  // previewKey is bumped by intentional events; adopt the latest doc then.
  useEffect(() => { adoptPreview() }, [previewKey, adoptPreview])

  // Entering visual mode → clear selection and adopt the latest doc so any code
  // or inspector edits made elsewhere are reflected.
  useEffect(() => {
    if (editorMode === 'visual') {
      setSelectedEl(null)
      setPreviewKey(k => k + 1)
    }
  }, [editorMode])

  // ── Variables schema (shared by header tabs + body table) ────────────────
  const varsSchema = site?.industry ? INDUSTRIES[site.industry] : null
  const activeVarsTabSchema = varsSchema ? (varsSchema.tabs.find(t => t.id === varsTab) || varsSchema.tabs[0]) : null

  const addCustomVariable = useCallback(() => {
    const key = window.prompt('Variable name (letters, numbers, underscores):')
    if (!key) return
    const clean = key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_')
    if (!clean) return
    const next = { ...varValues, [clean]: '' }
    setVarValues(next)
    setEditingVar(clean)
    setEditingVarValue('')
  }, [varValues])

  // ── Last-saved label ─────────────────────────────────────────────────────
  const lastSavedLabel = lastSaved
    ? (() => {
        const diffMs  = Date.now() - lastSaved.getTime()
        const diffMin = Math.floor(diffMs / 60000)
        if (diffMin < 1) return 'just now'
        if (diffMin === 1) return '1 minute ago'
        return `${diffMin} minutes ago`
      })()
    : null

  // ── Site selection screen ─────────────────────────────────────────────────
  if (!siteId) {
    const pickStatus = (st) => PICK_STATUS[st] || PICK_STATUS.draft
    return (
      <div className="edit-pick-page">
        {/* Breadcrumb sub-header */}
        <div className="edit-pick-bar">
          <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate(sitesPath)}>
            <ArrowLeft size={14} /> Back to {isClient ? 'My Sites' : 'Sites'}
          </button>
          <span style={{ width: 1, height: 20, background: 'var(--border)' }} />
          <span className="edit-pick-crumb"><Code2 size={18} /> Visual Editor</span>
        </div>

        <div className="edit-pick-wrap">
          <div className="edit-pick-card card">
            <div className="edit-pick-hero">
              <span className="edit-pick-hero-icon"><Search size={28} /></span>
              <h1>Select a Website to Edit</h1>
              <p>Search and select a site to open in the visual editor</p>
            </div>

            <div className="edit-pick-search">
              <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                className="input"
                autoFocus
                placeholder="Search by site name or company…"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <button className="site-action" title="Clear" onClick={() => handleSearchChange({ target: { value: '' } })}>
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="edit-pick-results">
              <div className="edit-pick-label">
                {searchQuery.trim() ? `${filteredSites.length} result${filteredSites.length === 1 ? '' : 's'}` : 'Recently edited'}
              </div>
              <div className="edit-pick-list">
                {filteredSites.map((s) => {
                  const [g1, g2] = pickGradient(s)
                  const st = pickStatus(s.status)
                  return (
                    <button key={s.id} className="edit-pick-row" onClick={() => handleSiteSelect(s)}>
                      <span className="edit-pick-thumb" style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}>
                        {(s.name || '?').replace(/^https?:\/\//, '').slice(0, 1).toUpperCase()}
                      </span>
                      <span className="edit-pick-main">
                        <span className="edit-pick-name">{s.name}</span>
                        <span className="edit-pick-sub">{s.company_name || '—'}</span>
                        <span className="edit-pick-meta"><Clock size={12} /> Updated {relativeTime(s.updated_at)}</span>
                      </span>
                      <span className={`badge${st.tone ? ' badge-' + st.tone : ''}`} style={{ flexShrink: 0 }}>
                        {st.dot && <span className="badge-dot" />}{st.label}
                      </span>
                      <span className="edit-pick-go"><ArrowRight size={16} /></span>
                    </button>
                  )
                })}
                {searchQuery.trim() && filteredSites.length === 0 && (
                  <div className="edit-pick-empty">
                    <span className="icon-tile" style={{ width: 40, height: 40, fontSize: 18, margin: '0 auto 12px' }}><Search size={18} /></span>
                    <div style={{ fontWeight: 500, color: 'var(--text-soft)' }}>No sites match “{searchQuery}”</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Try a different site name or company.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (siteLoading || pagesLoading) {
    return (
      <div className="editor-shell">
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner size="lg" text="Loading editor..." />
        </div>
      </div>
    )
  }

  // ── No pages yet ──────────────────────────────────────────────────────────
  if (!pagesLoading && pages.length === 0) {
    return (
      <div className="editor-shell">
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>This site has no pages yet.</p>
          <Button
            onClick={() => addPageMutation.mutate({ name: 'Home', slug: '/', is_home: true })}
            disabled={addPageMutation.isLoading}
          >
            {addPageMutation.isLoading ? 'Creating...' : 'Create Home Page'}
          </Button>
        </div>
      </div>
    )
  }

  // ─── Main editor shell ────────────────────────────────────────────────────
  return (
    <div className="editor-shell">

      {/* ── Topbar (floating card) ── */}
      <div className="editor-topbar">
        <button
          className="editor-topbar-btn"
          onClick={() => navigate(isClient ? sitesPath : (clientId ? `/admin/clients/${clientId}` : '/admin/sites'))}
        >
          <ArrowLeft size={14} /> {isClient ? 'My Sites' : 'Sites'}
        </button>
        <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, minWidth: 0 }}>
          <span style={{ width: 16, height: 16, borderRadius: 5, background: 'var(--accent)', flexShrink: 0, display: 'inline-block' }} />
          <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{site?.company_name || '—'} /</span>
          <span style={{ color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{site?.name || 'Untitled'}</span>
          {activePage && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>/</span>
              <div ref={pageMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  className="btn btn-sm btn-ghost"
                  style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setPageMenuOpen(o => !o)}
                >
                  {activePage.name} <ChevronDown size={11} style={{ transform: pageMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
                {pageMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      minWidth: 200,
                      maxHeight: 320,
                      overflowY: 'auto',
                      background: 'var(--bg-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      boxShadow: 'var(--shadow-lg)',
                      padding: 4,
                      zIndex: 50,
                    }}
                  >
                    {pages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => { handlePageSwitch(page); setPageMenuOpen(false) }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          width: '100%',
                          textAlign: 'left',
                          padding: '7px 10px',
                          fontSize: 13,
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          background: activePage.id === page.id ? 'var(--surface-hover)' : 'transparent',
                          color: 'var(--text)',
                          fontWeight: activePage.id === page.id ? 600 : 400,
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { if (activePage.id !== page.id) e.currentTarget.style.background = 'var(--surface-hover)' }}
                        onMouseLeave={(e) => { if (activePage.id !== page.id) e.currentTarget.style.background = 'transparent' }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.name}</span>
                        {page.is_home ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Home</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Device: icon-only segmented */}
        <div className="segmented" style={{ marginRight: 4 }}>
          {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([key, Icon]) => (
            <button key={key} className={device === key ? 'on' : ''} onClick={() => setDevice(key)} title={key}>
              <Icon size={13} />
            </button>
          ))}
        </div>

        {/* Undo/Redo (snapshot history across visual + code edits) */}
        <button
          className="editor-topbar-btn icon-only"
          title="Undo (⌘Z)"
          onClick={undo}
          disabled={!canUndo}
          style={!canUndo ? { opacity: 0.4, cursor: 'default' } : undefined}
        >
          <RotateCcw size={13} />
        </button>
        <button
          className="editor-topbar-btn icon-only"
          title="Redo (⌘⇧Z)"
          onClick={redo}
          disabled={!canRedo}
          style={!canRedo ? { opacity: 0.4, cursor: 'default' } : undefined}
        >
          <RotateCw size={13} />
        </button>

        <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />

        {/* Save status */}
        <button className="editor-topbar-btn" onClick={handleSave} disabled={savePageMutation.isLoading}>
          {savePageMutation.isLoading
            ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} />
            : <span className="dot-live" style={hasUnsaved ? { background: '#f59e0b' } : undefined} />}
          {savePageMutation.isLoading ? 'Saving…' : hasUnsaved ? 'Save draft' : 'Saved'}
        </button>

        <button
          className="editor-topbar-btn"
          onClick={() => { commitRef.current?.(); setPreviewKey(k => k + 1); setShowPreview(true) }}
        >
          <Eye size={13} /> Preview
        </button>

        <button
          className="editor-topbar-btn accent"
          onClick={handlePublish}
          disabled={isPublishing || publishMutation.isLoading}
        >
          {isPublishing
            ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
            : <Globe size={13} />}
          Publish
        </button>

        {/* Avatar */}
        <div style={{ marginLeft: 4, display: 'flex' }}>
          <div className="avatar avatar-grad-1" style={{ width: 26, height: 26, fontSize: 10 }}>
            {(user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
        </div>
      </div>

      {/* ── Mode strip (floating card) ── */}
      <div className="editor-mode-strip">
        {[
          { id: 'visual',    label: 'Visual',    Icon: Edit2 },
          { id: 'html',      label: 'HTML',      Icon: FileCode,  scope: 'Sections', code: true },
          { id: 'css',       label: 'CSS',        Icon: Palette,   scope: 'Global', code: true },
          { id: 'js',        label: 'JS',         Icon: Code2,     scope: 'Global', code: true },
          { id: 'brand',     label: 'Brand',      Icon: Palette },
          { id: 'variables', label: 'Variables',  Icon: Braces },
          { id: 'media',     label: 'Media',      Icon: Image },
        ].filter(m => !(isClient && m.code)).map(({ id, label, Icon, scope }) => (
          <button
            key={id}
            className={`mode-btn ${editorMode === id ? 'active' : ''}`}
            onClick={() => setEditorMode(id)}
          >
            <Icon size={14} />
            {label}
            {scope && <span className="scope-pill">{scope}</span>}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => { const blob = new Blob([previewDoc], { type: 'text/html' }); window.open(URL.createObjectURL(blob), '_blank') }}
        >
          <Eye size={12} /> Live preview
        </button>
      </div>

      {/* ── Visual mode: left panel + canvas + right panel ── */}
      {editorMode === 'visual' && (
        <>
          {/* Left panel: Pages/Layers/Assets */}
          <div className="editor-panel left">
            <div className="panel-header">
              <div className="segmented" style={{ flex: 1 }}>
                {['Pages', 'Layers', 'Assets'].map(tab => (
                  <button
                    key={tab}
                    className={sidebarTab === tab ? 'on' : ''}
                    onClick={() => setSidebarTab(tab)}
                    style={{ flex: 1 }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 8 }}>
              {/* Pages tab */}
              {sidebarTab === 'Pages' && (
                <div>
                  {pages.map(page => (
                    <button
                      key={page.id}
                      className={`layer-row ${activePage?.id === page.id ? 'selected' : ''}`}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}
                      onClick={() => handlePageSwitch(page)}
                    >
                      {page.is_home
                        ? <Home size={13} style={{ flexShrink: 0 }} />
                        : <FileCode size={13} style={{ flexShrink: 0 }} />}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {page.name}
                      </span>
                      {!page.is_home && (
                        <span
                          onClick={e => { e.stopPropagation(); setDeletingPageId(page.id) }}
                          style={{ color: 'var(--text-faint)', cursor: 'pointer' }}
                          title="Delete"
                        >
                          <X size={11} />
                        </span>
                      )}
                    </button>
                  ))}
                  <button
                    className="layer-row"
                    style={{ width: '100%', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 8 }}
                    onClick={() => setShowAddPage(true)}
                  >
                    <Plus size={13} style={{ flexShrink: 0 }} /> Add page
                  </button>
                </div>
              )}

              {sidebarTab === 'Layers' && (
                layerTree.length > 0 ? (
                  <div>
                    {flattenLayers(layerTree).map((l) => {
                      const Ic = LAYER_ICON[l.tag] || Square
                      const isSel = selectedEl?.id === l.id
                      return (
                        <button
                          key={l.id}
                          className={`layer-row ${isSel ? 'selected' : ''}`}
                          style={{ width: '100%', paddingLeft: 8 + l.depth * 14, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}
                          onClick={() => postToIframe({ type: 'wn-select-id', id: l.id })}
                          title={l.text || l.tag}
                        >
                          <Ic size={13} style={{ color: l.tag.match(/^(h[1-6]|p|span|a)$/) ? 'var(--accent-3)' : 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.label}</span>
                          {l.text && <span style={{ fontSize: 10, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 70 }}>{l.text}</span>}
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <div className="cmd-empty">
                    <Layers size={22} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    <p>No elements yet — add HTML first</p>
                  </div>
                )
              )}

              {sidebarTab === 'Assets' && (
                <div>
                  {mediaItems.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {mediaItems.slice(0, 8).map((item, i) => {
                        const url = item.thumbnail_url || item.public_url || item.url
                        return (
                          <button
                            key={i}
                            onClick={() => insertTextAtCursor(`${url}`)}
                            style={{ aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--bg-0)' }}
                          >
                            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="cmd-empty">
                      <Image size={22} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                      <p>No assets yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => setShowAddPage(true)}>
                <Plus size={12} /> Add page
              </button>
            </div>
          </div>

          {/* Canvas */}
          <div className="canvas-area">
            <div className="canvas-toolbar">
              <button className="btn btn-sm btn-ghost btn-icon" onClick={refreshPreview} title="Refresh preview">
                <RefreshCw size={12} />
              </button>
              {selectedEl && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="dot-live" style={{ width: 5, height: 5, background: 'var(--accent)' }} />
                  Selected <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-3)' }}>&lt;{selectedEl.tag}&gt;</code>
                </span>
              )}
              {publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-ghost"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ExternalLink size={12} /> Live site
                </a>
              )}
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                {device === 'desktop' ? '1440' : device === 'tablet' ? '768' : '390'}px · 100%
              </span>
            </div>
            <div className="canvas-stage">
              {!code.html ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'var(--text-faint)', textAlign: 'center', padding: 40 }}>
                  <FileCode size={48} style={{ opacity: 0.25 }} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-soft)', marginBottom: 6 }}>Nothing to preview yet</div>
                    <div style={{ fontSize: 13 }}>
                      {isClient
                        ? 'Your site is still being set up. Check back soon, or use Variables and Media to add your content.'
                        : "Switch to the HTML tab to start writing your site's markup."}
                    </div>
                  </div>
                  {!isClient && (
                    <button className="btn btn-primary btn-sm" onClick={() => setEditorMode('html')}>
                      <FileCode size={12} /> Open HTML editor
                    </button>
                  )}
                </div>
              ) : (
                <div
                  className="device-frame"
                  style={{ width: DEVICE_SIZES[device].width, maxWidth: '100%' }}
                >
                  <iframe
                    key={rendered.ver}
                    ref={iframeRef}
                    srcDoc={rendered.doc}
                    title="Site Preview"
                    style={{ width: '100%', height: '100%', minHeight: '600px', border: 'none', display: 'block' }}
                    sandbox="allow-scripts allow-forms allow-popups"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right panel: Inspector — driven by iframe selection */}
          <div className="editor-panel right">
            <div className="panel-header" style={{ justifyContent: 'space-between' }}>
              <div>
                <div className="panel-title">Inspector</div>
                {selectedEl && (
                  <div style={{ fontSize: 12.5, fontWeight: 500, marginTop: 4 }}>
                    {selectedEl.tag === 'h1' ? 'Heading' : selectedEl.hasText ? 'Text' : selectedEl.tag}
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, fontFamily: 'var(--font-mono)', marginLeft: 6, textTransform: 'uppercase' }}>{selectedEl.tag}</span>
                  </div>
                )}
              </div>
              {selectedEl && (
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { postToIframe({ type: 'wn-deselect' }); setSelectedEl(null) }} title="Deselect">
                  <X size={13} />
                </button>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 14px' }}>
              {!selectedEl ? (
                /* Nothing selected — show page info + hint */
                <>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', marginBottom: 8 }}>Page</div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{activePage?.name || 'Home'}</div>
                    {activePage?.slug && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>/{activePage.slug.replace(/^\//, '')}</div>
                    )}
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Eye size={14} style={{ color: 'var(--accent-3)' }} />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Click to edit</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      Click any element in the preview to select it, then edit its text and styling here — your changes are written straight back into the page and saved with the site.
                    </div>
                  </div>
                  {hasUnsaved && (
                    <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}>
                      <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 500 }}>Unsaved changes</div>
                      <button className="btn btn-sm" style={{ marginTop: 8, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={handleSave}>Save draft</button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Content */}
                  {selectedEl.hasText && (
                    <InspectorSection title="Content">
                      <textarea
                        className="input"
                        style={{ minHeight: 60, resize: 'vertical', width: '100%' }}
                        value={selectedEl.text}
                        onChange={(e) => applyText(e.target.value)}
                      />
                    </InspectorSection>
                  )}

                  {/* Typography */}
                  {!isClient && selectedEl.hasText && (
                    <InspectorSection title="Typography">
                      <PropRow label="Weight">
                        <div className="segmented" style={{ width: '100%' }}>
                          {['400', '500', '600', '700'].map(w => (
                            <button key={w} className={String(selectedEl.styles.fontWeight) === w ? 'on' : ''} style={{ flex: 1 }} onClick={() => applyStyle('fontWeight', w)}>{w}</button>
                          ))}
                        </div>
                      </PropRow>
                      <PropRow label="Size">
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                          <div style={{ width: 64, flexShrink: 0 }}>
                            <SmallInput value={String(selectedEl.styles.fontSize)} suffix="px" onChange={(v) => applyStyle('fontSize', `${parseInt(v) || 0}px`)} />
                          </div>
                          <input type="range" min="10" max="120" value={selectedEl.styles.fontSize} onChange={(e) => applyStyle('fontSize', `${e.target.value}px`)} style={{ flex: 1, minWidth: 0, accentColor: 'var(--accent)' }} />
                        </div>
                      </PropRow>
                      <PropRow label="Color">
                        <ColorPick value={selectedEl.styles.color} onChange={(v) => applyStyle('color', v)} />
                      </PropRow>
                    </InspectorSection>
                  )}

                  {/* Layout */}
                  {!isClient && (
                  <InspectorSection title="Layout">
                    <PropRow label="Padding">
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6 }}>
                        <SmallInput label="X" value={String(selectedEl.styles.paddingX)} onChange={(v) => applyBox('paddingX', ['paddingLeft', 'paddingRight'], v)} />
                        <SmallInput label="Y" value={String(selectedEl.styles.paddingY)} onChange={(v) => applyBox('paddingY', ['paddingTop', 'paddingBottom'], v)} />
                      </div>
                    </PropRow>
                    <PropRow label="Margin">
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6 }}>
                        <SmallInput label="↑" value={String(selectedEl.styles.marginTop)} onChange={(v) => applyBox('marginTop', ['marginTop'], v)} />
                        <SmallInput label="↓" value={String(selectedEl.styles.marginBottom)} onChange={(v) => applyBox('marginBottom', ['marginBottom'], v)} />
                      </div>
                    </PropRow>
                    <PropRow label="Radius">
                      <SmallInput value={String(selectedEl.styles.radius)} suffix="px" onChange={(v) => applyStyle('borderRadius', `${parseInt(v) || 0}px`)} />
                    </PropRow>
                  </InspectorSection>
                  )}

                  {/* Appearance */}
                  {!isClient && (
                  <InspectorSection title="Appearance">
                    <PropRow label="Background">
                      <ColorPick value={selectedEl.styles.background} onChange={(v) => applyStyle('background', v)} />
                    </PropRow>
                  </InspectorSection>
                  )}

                  {isClient && selectedEl && !selectedEl.hasText && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      This element has no editable text. Pick a heading, paragraph, or button to edit its words, or use the <strong>Variables</strong> and <strong>Media</strong> tabs.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Autosave footer */}
            <div style={{ padding: 10, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-faint)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="dot-live" style={{ width: 5, height: 5 }} /> {lastSavedLabel ? `Saved ${lastSavedLabel}` : 'All changes autosaved'}
            </div>
          </div>
        </>
      )}

      {/* ── Code modes: full-width code-view-container ── */}
      {editorMode !== 'visual' && (
        <div className="code-view-container">
          {/* Header: mode title */}
          <div className="code-mode-header">
            {editorMode === 'html' && (
              <>
                <span className="code-mode-title"><FileCode size={14} /> HTML</span>
                <span className="badge" style={{ fontFamily: 'var(--font-mono)' }}>Sections</span>
              </>
            )}
            {editorMode === 'css' && (
              <>
                <span className="code-mode-title"><Palette size={14} /> CSS</span>
                <span className="badge" style={{ fontFamily: 'var(--font-mono)' }}>styles.css</span>
              </>
            )}
            {editorMode === 'js' && (
              <>
                <span className="code-mode-title"><Code2 size={14} /> JavaScript</span>
                <span className="badge" style={{ fontFamily: 'var(--font-mono)' }}>main.js</span>
              </>
            )}
            {editorMode === 'brand' && <span className="code-mode-title"><Palette size={14} /> Brand</span>}
            {editorMode === 'variables' && (
              varsSchema ? (
                <div className="section-tabs">
                  {varsSchema.tabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`section-tab ${(varsTab || varsSchema.tabs[0].id) === tab.id ? 'active' : ''}`}
                      onClick={() => setVarsTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ) : <span className="code-mode-title"><Braces size={14} /> Variables</span>
            )}
            {editorMode === 'media' && <span className="code-mode-title"><Image size={14} /> Media library</span>}
            <div style={{ flex: 1 }} />
            {editorMode === 'brand' && (
              <button className="btn btn-sm btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={saveBrand} disabled={saveMetaMutation.isLoading}>
                {saveMetaMutation.isLoading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
            )}
            {editorMode === 'variables' && varsSchema && (
              <button className="btn btn-sm btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={addCustomVariable}>
                <Plus size={12} /> Add variable
              </button>
            )}
            {['html', 'css', 'js'].includes(editorMode) && (
              <>
                <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={refreshPreview}>
                  <RefreshCw size={12} /> Refresh preview
                </button>
                <button className="btn btn-sm btn-ghost btn-icon" onClick={() => { const blob = new Blob([previewDoc], { type: 'text/html' }); window.open(URL.createObjectURL(blob), '_blank') }} title="Open in new tab">
                  <ExternalLink size={12} />
                </button>
              </>
            )}
          </div>

          {/* HTML: section tabs + variable pills (mirrors prototype) */}
          {editorMode === 'html' && varsSchema && activeVarsTabSchema && (
            <>
              {/* Section tabs (faded industry icon + underline-active tabs) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 16px 11px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, marginRight: 6, opacity: 0.45, flexShrink: 0 }}>{varsSchema.icon}</span>
                <div className="section-tabs">
                  {varsSchema.tabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`section-tab ${(varsTab || varsSchema.tabs[0].id) === tab.id ? 'active' : ''}`}
                      onClick={() => setVarsTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Variable pills */}
              <div className="var-pills-row" style={{ maxHeight: 150, overflowY: 'auto' }}>
                <span className="var-pills-label">Variables</span>
                {activeVarsTabSchema.fields.map(field => {
                  if (field.type === 'list') {
                    const subKeys = field.subFields.map(sf => `  {{${sf.key}}}`).join('\n')
                    const loopSnippet = `{{#${field.key}}}\n${subKeys}\n{{/${field.key}}}`
                    return (
                      <button key={field.key} className="var-pill" title={`Insert ${field.label} loop`} onClick={() => insertTextAtCursor(loopSnippet)}>
                        <span className="var-pill-token">{`{{#${field.key}}}`}</span>
                        <span className="var-pill-label">{field.label}</span>
                      </button>
                    )
                  }
                  const token = `{{${field.key}}}`
                  return (
                    <button key={field.key} className="var-pill" title={`Insert ${token}`} onClick={() => insertTextAtCursor(token)}>
                      <span className="var-pill-token">{token}</span>
                      <span className="var-pill-label">{field.label}</span>
                    </button>
                  )
                })}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-faint)', flexShrink: 0 }}>click to insert at cursor</span>
              </div>
            </>
          )}

          {/* Brand mode — Identity / Logo & favicon / Colors / Typography */}
          {editorMode === 'brand' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 24, position: 'relative' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                {/* Identity */}
                <div className="card card-pad">
                  <h3 className="h3" style={{ marginBottom: 14 }}>Identity</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <BrandRow label="Site name">
                      <input className="input" value={brand.siteName} onChange={(e) => setBrand(b => ({ ...b, siteName: e.target.value }))} />
                    </BrandRow>
                    <BrandRow label="Tagline">
                      <input className="input" value={brand.tagline} onChange={(e) => setBrand(b => ({ ...b, tagline: e.target.value }))} placeholder="Single origin · Roasted weekly" />
                    </BrandRow>
                  </div>
                </div>

                {/* Logo & favicon */}
                <div className="card card-pad">
                  <h3 className="h3" style={{ marginBottom: 14 }}>Logo &amp; favicon</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <BrandRow label="Logo">
                      <AssetSlot value={brand.logo} onReplace={() => setAssetTarget('logo')} onClear={() => setBrand(b => ({ ...b, logo: '' }))} />
                    </BrandRow>
                    <BrandRow label="Favicon">
                      <AssetSlot small value={brand.favicon} onReplace={() => setAssetTarget('favicon')} onClear={() => setBrand(b => ({ ...b, favicon: '' }))} />
                    </BrandRow>
                  </div>
                </div>

                {/* Color palette */}
                <div className="card card-pad">
                  <h3 className="h3" style={{ marginBottom: 14 }}>Color palette</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <BrandRow label="Primary">
                      <SwatchRow value={brand.primary} options={['#7C5CFF', '#FF8A65', '#06B6D4', '#10B981', '#F59E0B', '#0F1020']} onChange={(v) => setBrand(b => ({ ...b, primary: v }))} />
                    </BrandRow>
                    <BrandRow label="Accent">
                      <SwatchRow value={brand.accent} options={['#5B7CFF', '#0F1020', '#7C5CFF', '#10B981']} onChange={(v) => setBrand(b => ({ ...b, accent: v }))} />
                    </BrandRow>
                    <BrandRow label="Background">
                      <SwatchRow value={brand.background} options={['#FFFFFF', '#FAF8F5', '#0F1020', '#181B36']} onChange={(v) => setBrand(b => ({ ...b, background: v }))} />
                    </BrandRow>
                  </div>
                </div>

                {/* Typography */}
                <div className="card card-pad">
                  <h3 className="h3" style={{ marginBottom: 14 }}>Typography</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <BrandRow label="Headings">
                      <NSSelect
                        value={brand.headingFont}
                        onChange={(e) => setBrand(b => ({ ...b, headingFont: e.target.value }))}
                        options={['Geist', 'Inter', 'Söhne', 'Tiempos Headline']}
                      />
                    </BrandRow>
                    <BrandRow label="Body">
                      <NSSelect
                        value={brand.bodyFont}
                        onChange={(e) => setBrand(b => ({ ...b, bodyFont: e.target.value }))}
                        options={['Inter', 'Geist', 'Söhne']}
                      />
                    </BrandRow>
                    <BrandRow label="Scale">
                      <div className="segmented" style={{ width: '100%' }}>
                        {['Tight', 'Modern', 'Loose'].map(s => (
                          <button key={s} className={brand.scale === s ? 'on' : ''} style={{ flex: 1 }} onClick={() => setBrand(b => ({ ...b, scale: s }))}>{s}</button>
                        ))}
                      </div>
                    </BrandRow>
                  </div>
                </div>
              </div>

              {/* Asset picker overlay */}
              {assetTarget && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(7,8,15,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, padding: 24 }} onClick={() => setAssetTarget(null)}>
                  <div className="card" style={{ width: 560, maxWidth: '100%', maxHeight: '80%', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>Pick {assetTarget}</span>
                      <button className="site-action" style={{ marginLeft: 'auto' }} onClick={() => setAssetTarget(null)}><X size={14} /></button>
                    </div>
                    <div style={{ padding: 16, overflowY: 'auto' }}>
                      {mediaItems.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10 }}>
                          {mediaItems.map((item, i) => {
                            const url = item.thumbnail_url || item.public_url || item.url || ''
                            return (
                              <button key={i} onClick={() => { setBrand(b => ({ ...b, [assetTarget]: url })); setAssetTarget(null) }}
                                style={{ aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--bg-0)' }}>
                                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="cmd-empty"><Image size={24} style={{ margin: '0 auto 8px', opacity: 0.3 }} /><p>No media uploaded yet</p></div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Variables mode */}
          {editorMode === 'variables' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {varsSchema && activeVarsTabSchema ? (() => {
                return (
                  <div>
                    <div className="card" style={{ overflow: 'hidden' }}>
                      <div className="vars-row vars-header">
                        <div>Token</div><div>Label</div><div>Current value</div><div></div>
                      </div>
                      {activeVarsTabSchema?.fields.map(field => {
                        const token = `{{${field.key}}}`
                        const isEditing = editingVar === field.key
                        const val = varValues[field.key]
                        return (
                          <div key={field.key} className="vars-row">
                            <span><span className="var-pill-token" style={{ fontSize: 12 }}>{token}</span></span>
                            <span style={{ color: 'var(--text-soft)', fontSize: 13 }}>{field.label}</span>
                            {isEditing ? (
                              <input
                                className="input"
                                autoFocus
                                value={editingVarValue}
                                onChange={(e) => setEditingVarValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { const next = { ...varValues, [field.key]: editingVarValue }; setVarValues(next); setEditingVar(null); saveVariables(next) }
                                  if (e.key === 'Escape') setEditingVar(null)
                                }}
                                placeholder="Set a value…"
                                style={{ fontSize: 12.5 }}
                              />
                            ) : (
                              <span style={{ fontSize: 12.5, color: val ? 'var(--text-soft)' : 'var(--text-faint)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {val || '— not set —'}
                              </span>
                            )}
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              {isEditing ? (
                                <>
                                  <button className="site-action" title="Save" onClick={() => { const next = { ...varValues, [field.key]: editingVarValue }; setVarValues(next); setEditingVar(null); saveVariables(next) }}>
                                    <Check size={13} style={{ color: 'var(--success)' }} />
                                  </button>
                                  <button className="site-action" title="Cancel" onClick={() => setEditingVar(null)}>
                                    <X size={13} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button className="site-action" title="Edit value" onClick={() => { setEditingVar(field.key); setEditingVarValue(val || '') }}>
                                    <Edit2 size={13} />
                                  </button>
                                  <button className="site-action" title="Insert token" onClick={() => insertTextAtCursor(token)}>
                                    <Copy size={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}

                      {/* Custom variables (not in the industry schema) */}
                      {Object.keys(varValues)
                        .filter(k => !varsSchema.tabs.some(t => t.fields.some(f => f.key === k)))
                        .map(key => {
                          const token = `{{${key}}}`
                          const isEditing = editingVar === key
                          const val = varValues[key]
                          return (
                            <div key={`custom-${key}`} className="vars-row">
                              <span><span className="var-pill-token" style={{ fontSize: 12 }}>{token}</span></span>
                              <span style={{ color: 'var(--text-faint)', fontSize: 12, fontStyle: 'italic' }}>custom</span>
                              {isEditing ? (
                                <input className="input" autoFocus value={editingVarValue} onChange={(e) => setEditingVarValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { const next = { ...varValues, [key]: editingVarValue }; setVarValues(next); setEditingVar(null); saveVariables(next) }
                                    if (e.key === 'Escape') setEditingVar(null)
                                  }}
                                  placeholder="Set a value…" style={{ fontSize: 12.5 }} />
                              ) : (
                                <span style={{ fontSize: 12.5, color: val ? 'var(--text-soft)' : 'var(--text-faint)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val || '— not set —'}</span>
                              )}
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                {isEditing ? (
                                  <>
                                    <button className="site-action" title="Save" onClick={() => { const next = { ...varValues, [key]: editingVarValue }; setVarValues(next); setEditingVar(null); saveVariables(next) }}><Check size={13} style={{ color: 'var(--success)' }} /></button>
                                    <button className="site-action" title="Cancel" onClick={() => setEditingVar(null)}><X size={13} /></button>
                                  </>
                                ) : (
                                  <>
                                    <button className="site-action" title="Edit value" onClick={() => { setEditingVar(key); setEditingVarValue(val || '') }}><Edit2 size={13} /></button>
                                    <button className="site-action" title="Remove" onClick={() => { const next = { ...varValues }; delete next[key]; setVarValues(next); saveVariables(next) }}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>
                                  </>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>

                    <div className="card card-pad" style={{ marginTop: 14, background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Braces size={16} style={{ color: 'var(--accent-3)', flexShrink: 0 }} />
                        <div style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.5 }}>
                          Variables are reusable values — set one here and every <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-3)' }}>{'{{token}}'}</span> in your HTML updates on publish. Values save to this site.
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })() : (
                <div className="cmd-empty">
                  <Braces size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <p>No industry variables for this site.</p>
                </div>
              )}
            </div>
          )}

          {/* Media mode */}
          {editorMode === 'media' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {mediaItems.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
                  {mediaItems.map((item, i) => {
                    const url = item.thumbnail_url || item.public_url || item.url || ''
                    return (
                      <button
                        key={i}
                        onClick={() => insertMediaAtCursor(url)}
                        style={{ aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--bg-0)', display: 'block' }}
                        title={item.original_name}
                      >
                        <img src={url} alt={item.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="cmd-empty">
                  <Image size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <p>No media uploaded for this client yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Monaco for html/css/js — full dark code surface + footer */}
          {['html', 'css', 'js'].includes(editorMode) && (
            <div className="code-area" style={{ flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
                {CODE_TABS.map(({ id, language }) => (
                  <div
                    key={id}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      visibility: editorMode === id ? 'visible' : 'hidden',
                      pointerEvents: editorMode === id ? 'auto' : 'none',
                    }}
                  >
                    <Editor
                      height="100%"
                      language={language}
                      value={code[id]}
                      theme={monacoTheme}
                      beforeMount={handleEditorBeforeMount}
                      onChange={(val) => handleCodeChange(val, id)}
                      onMount={(editor) => { editorRefs.current[id] = editor }}
                      options={{
                        fontSize: 13,
                        fontFamily: '"Geist Mono", "JetBrains Mono", monospace',
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'on',
                        folding: true,
                        automaticLayout: true,
                        tabSize: 2,
                        padding: { top: 12, bottom: 12 },
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* Footer bar (prototype) */}
              <div className="code-foot">
                <span>{editorMode === 'js' ? 'JavaScript' : editorMode.toUpperCase()}</span>
                <span>·</span>
                <span>{(code[editorMode] || '').split('\n').length} lines</span>
                <span>·</span>
                <span>UTF-8</span>
                <span style={{ marginLeft: 'auto' }}>{hasUnsaved ? 'Modified' : 'Saved'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Full-screen preview overlay (redesigned browser chrome, live doc) ── */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', background: 'var(--bg-0)' }}>
          {/* Browser chrome */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', flexShrink: 0 }}>
            <button className="editor-topbar-btn" onClick={() => setShowPreview(false)}>
              <ArrowLeft size={14} /> Back to editor
            </button>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--danger)' }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--warning)' }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--success)' }} />
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 999, maxWidth: 520, margin: '0 auto', fontSize: 12.5, color: 'var(--text-soft)', fontFamily: 'var(--font-mono)' }}>
              <Globe size={12} style={{ color: 'var(--success)', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {publicUrl ? publicUrl.replace('https://', '') : `${site?.slug || 'preview'} · draft`}
                {activePage && activePage.slug ? `/${activePage.slug.replace(/^\//, '')}` : ''}
              </span>
            </div>
            {/* device toggle mirrors editor device */}
            <div className="segmented">
              {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([key, Ic]) => (
                <button key={key} className={device === key ? 'on' : ''} onClick={() => setDevice(key)} title={key}><Ic size={13} /></button>
              ))}
            </div>
            {publicUrl && (
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="editor-topbar-btn">
                <ExternalLink size={13} /> Visit live site
              </a>
            )}
          </div>
          {/* Rendered preview */}
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', background: 'var(--bg-2)', padding: device === 'desktop' ? 0 : 24 }}>
            <iframe
              key={`fp-${previewKey}`}
              title="Full preview"
              srcDoc={previewDoc}
              sandbox="allow-scripts allow-forms allow-popups"
              style={{ width: DEVICE_SIZES[device].width, maxWidth: '100%', height: '100%', minHeight: '100%', border: 'none', background: 'white', boxShadow: device === 'desktop' ? 'none' : 'var(--shadow-lg)', borderRadius: device === 'mobile' ? 20 : 0 }}
            />
          </div>
        </div>
      )}

      {/* ── Add page modal ── */}
      {showAddPage && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowAddPage(false)}
        >
          <div
            style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, margin: '0 16px', boxShadow: '0 24px 64px rgba(0,0,0,.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Add New Page</h3>
              <button onClick={() => setShowAddPage(false)} style={{ color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Page name</label>
                <input
                  type="text"
                  value={newPageName}
                  onChange={handlePageNameChange}
                  placeholder="e.g. Menu, About, Contact"
                  autoFocus
                  className="ds-input w-full"
                  onKeyDown={e => e.key === 'Enter' && handleAddPage()}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>URL slug</label>
                <input
                  type="text"
                  value={newPageSlug}
                  onChange={e => setNewPageSlug(e.target.value)}
                  placeholder="/menu"
                  className="ds-input w-full"
                  style={{ fontFamily: 'monospace' }}
                />
                <p style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
                  Links to this slug in your HTML will switch to this page in the preview
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <Button variant="outline" style={{ flex: 1 }} onClick={() => setShowAddPage(false)}>Cancel</Button>
              <Button style={{ flex: 1 }} onClick={handleAddPage} disabled={!newPageName.trim() || addPageMutation.isLoading}>
                {addPageMutation.isLoading ? <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} /> : <Plus size={14} style={{ marginRight: 6 }} />}
                Create page
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete page modal ── */}
      {deletingPageId && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setDeletingPageId(null)}
        >
          <div
            style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, margin: '0 16px', boxShadow: '0 24px 64px rgba(0,0,0,.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ padding: 8, background: 'rgba(239,68,68,.1)', borderRadius: 10 }}>
                <Trash2 size={18} style={{ color: '#ef4444' }} />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Delete page?</h3>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              "{pages.find(p => p.id === deletingPageId)?.name}" will be permanently deleted. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" style={{ flex: 1 }} onClick={() => setDeletingPageId(null)}>Cancel</Button>
              <Button
                variant="destructive"
                style={{ flex: 1 }}
                onClick={() => deletePageMutation.mutate(deletingPageId)}
                disabled={deletePageMutation.isLoading}
              >
                {deletePageMutation.isLoading ? <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} /> : <Trash2 size={14} style={{ marginRight: 6 }} />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Media picker modal ── */}
      {showMediaPicker && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowMediaPicker(false)}
        >
          <div
            style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 640, margin: '0 16px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Media Library</h3>
                <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 2 }}>Click an image to insert it into your {activeCodeTab.toUpperCase()}</p>
              </div>
              <button onClick={() => setShowMediaPicker(false)} style={{ color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {mediaItems.length === 0 ? (
                <div className="cmd-empty">
                  <Image size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                  <p>No media uploaded for this client yet</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {mediaItems.map((item) => {
                    const url = item.public_url || item.file_path || ''
                    return (
                      <button
                        key={item.id}
                        onClick={() => insertMediaAtCursor(url)}
                        style={{ position: 'relative', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--bg-0)' }}
                        title={item.original_name}
                      >
                        <img src={item.thumbnail_url || url} alt={item.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div
                          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: 'background .15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,.35)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
                        >
                          <Copy size={18} style={{ color: 'white', opacity: 0 }} />
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default NewEditor
